import React, { useEffect, useState } from 'react';
import styles from './UserWorkoutsRow.module.scss';
import classNames from 'classnames';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import { getFullName, calculateUserStatistics } from '../../utils';
import Menu from '../atoms/Menu';
import VelocityPie from './VelocityPie';
import ConfirmationDialog from '../DialogWrappers/ConfirmationDialog';
import AssignProgramOptionsDialog from '../Programs/AssignProgramOptionsDialog';
import AssignProgramDialog from '../Programs/AssignProgramDialog';
import { UserType } from '../../constants';
import { useUserAction } from '../../providers';
import { locales } from '../../constants/locale';

import type {
  AssignedProgram,
  Group,
  Member,
  Trainer,
  SessionStatistic,
  UserStatistics,
} from '../../types';

const texts = locales.en.components.clients.userWorkoutsRow;

interface UserWorkoutsRowProps {
  groups: Group[];
  selected: boolean;
  onSelect: () => void;
  member: Member;
  trainer: Trainer | undefined;
  openEditClientModal: () => void;
  archiveMember: (member: Member) => Promise<void>;
  userType: UserType;
  onEditProgram: () => void;
  onCreateNewProgram: () => void;
  lastWorkoutSessionStatistic: SessionStatistic | null;
  last7DaysSessionStatistics: SessionStatistic[];
  last30DaysSessionStatistics: SessionStatistic[];
}

const UserWorkoutsRow: React.FC<UserWorkoutsRowProps> = ({
  groups,
  userType,
  member,
  trainer,
  onSelect,
  selected,
  archiveMember,
  openEditClientModal,
  onEditProgram,
  onCreateNewProgram,
  lastWorkoutSessionStatistic,
  last7DaysSessionStatistics,
  last30DaysSessionStatistics,
}) => {
  const { assignProgramAndRefreshMembers } = useUserAction();
  const [isDeleteClientConfirmationOpen, setIsDeleteClientConfirmationOpen] =
    useState(false);
  const [isAssignProgramModalOpen, setIsAssignProgramModalOpen] =
    useState(false);
  const [isAddProgramTemplateModalOpen, setIsAddProgramTemplateModalOpen] =
    useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [userStatistics, setUserStatistics] = useState<UserStatistics | null>(
    null,
  );
  useEffect(() => {
    const statistics = calculateUserStatistics(
      lastWorkoutSessionStatistic,
      last7DaysSessionStatistics,
      last30DaysSessionStatistics,
      [],
    );
    setUserStatistics(statistics);
  }, [
    lastWorkoutSessionStatistic,
    last7DaysSessionStatistics,
    last30DaysSessionStatistics,
  ]);

  const handleCloseMenuOptions = () => {
    setAnchorEl(null);
  };

  const handleEditClient = () => {
    openEditClientModal();
    handleCloseMenuOptions();
  };

  const handleDeleteClient = async () => {
    handleCloseMenuOptions();
    await archiveMember(member);
    setIsDeleteClientConfirmationOpen(false);
  };

  const renderUserName = () => (
    <div className={classNames(styles.userColumn, styles.userName)}>
      <AccountCircleOutlinedIcon className={styles.userIcon} color="primary" />
      {getFullName(member)}
    </div>
  );

  const handleAssignProgramFromTemplate = async (program: AssignedProgram) => {
    await assignProgramAndRefreshMembers([member.id], program);
    setIsAddProgramTemplateModalOpen(false);
  };

  const renderAssignProgramOptionsDialog = () => {
    if (!isAssignProgramModalOpen) return null;
    return (
      <AssignProgramOptionsDialog
        onCreate={() => {
          setIsAssignProgramModalOpen(false);
          onCreateNewProgram();
        }}
        onAdd={() => {
          setIsAssignProgramModalOpen(false);
          setIsAddProgramTemplateModalOpen(true);
        }}
        onClose={() => setIsAssignProgramModalOpen(false)}
      />
    );
  };

  const renderProgramTemplateDialog = () => (
    <AssignProgramDialog
      onSubmit={handleAssignProgramFromTemplate}
      isOpen={isAddProgramTemplateModalOpen}
      onClose={() => setIsAddProgramTemplateModalOpen(false)}
    />
  );

  const renderDeleteClientConfirmationDialog = () => (
    <ConfirmationDialog
      text={`Are you sure you want to delete this ${userType.toLowerCase()}?`}
      onSubmit={handleDeleteClient}
      submitText="Yes"
      cancelText="No"
      isOpen={isDeleteClientConfirmationOpen}
      onCancel={() => setIsDeleteClientConfirmationOpen(false)}
      symbol={<DeleteForeverOutlinedIcon />}
    />
  );

  const getUserOptions = () => {
    return [
      {
        onClick: handleEditClient,
        label: `Edit ${userType} Details`,
        icon: <BorderColorOutlinedIcon color="primary" />,
      },
      {
        onClick: () => {
          handleCloseMenuOptions();
          setIsDeleteClientConfirmationOpen(true);
        },
        label: `Delete ${userType}`,
        icon: <DeleteForeverOutlinedIcon color="primary" />,
      },
      {
        onClick: () => {
          handleCloseMenuOptions();
          setIsAssignProgramModalOpen(true);
        },
        label: 'Assign Program',
        icon: <PersonAddAlt1OutlinedIcon color="primary" />,
      },
    ];
  };

  const getEnrichedUserOptions = () => {
    const options = getUserOptions();
    if (member.program) {
      options.push({
        onClick: () => {
          handleCloseMenuOptions();
          onEditProgram();
        },
        label: 'Edit Program',
        icon: <AppRegistrationIcon color="primary" />,
      });
    }
    return options;
  };

  const renderMoreOptions = () => (
    <div className={classNames(styles.userColumn, styles.options)}>
      <PendingOutlinedIcon
        // @ts-ignore
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget as unknown as HTMLElement);
        }}
        className={styles.optionsBtn}
        color="primary"
      />
      <Menu
        anchorEl={anchorEl}
        isOpen={open}
        onClose={handleCloseMenuOptions}
        options={getEnrichedUserOptions()}
      />
    </div>
  );

  const renderLastWorkoutVelocityPie = () => {
    const {
      lastWorkoutConcentricGoodRepsPercentage,
      lastWorkoutConcentricFastRepsPercentage,
      lastWorkoutConcentricSlowRepsPercentage,
    } = userStatistics || {};

    if (
      !lastWorkoutConcentricGoodRepsPercentage &&
      !lastWorkoutConcentricFastRepsPercentage &&
      !lastWorkoutConcentricSlowRepsPercentage
    ) {
      return (
        <div className={styles.velocityPieColumn}>
          <span className={styles.emptyStat}>-</span>
        </div>
      );
    }
    return (
      <div className={styles.velocityPieColumn}>
        <VelocityPie
          good={lastWorkoutConcentricGoodRepsPercentage}
          fast={lastWorkoutConcentricFastRepsPercentage}
          slow={lastWorkoutConcentricSlowRepsPercentage}
          missed={userStatistics?.lastWorkoutConcentricMissedRepsPercentage}
          subtitle={texts.lastWorkout}
        />
      </div>
    );
  };

  const renderLastWeekVelocityPie = () => {
    const {
      lastWeekConcentricGoodRepsPercentage,
      lastWeekConcentricFastRepsPercentage,
      lastWeekConcentricSlowRepsPercentage,
    } = userStatistics || {};

    if (
      !lastWeekConcentricGoodRepsPercentage &&
      !lastWeekConcentricFastRepsPercentage &&
      !lastWeekConcentricSlowRepsPercentage
    ) {
      return (
        <div className={styles.velocityPieColumn}>
          <span className={styles.emptyStat}>-</span>
        </div>
      );
    }
    return (
      <div className={styles.velocityPieColumn}>
        <VelocityPie
          good={lastWeekConcentricGoodRepsPercentage}
          fast={lastWeekConcentricFastRepsPercentage}
          slow={lastWeekConcentricSlowRepsPercentage}
          missed={userStatistics?.lastWeekConcentricMissedRepsPercentage}
          subtitle={texts.last7Days}
        />
      </div>
    );
  };

  const renderLastMonthVelocityPie = () => {
    const {
      lastMonthConcentricGoodRepsPercentage,
      lastMonthConcentricFastRepsPercentage,
      lastMonthConcentricSlowRepsPercentage,
    } = userStatistics || {};

    if (
      !lastMonthConcentricGoodRepsPercentage &&
      !lastMonthConcentricFastRepsPercentage &&
      !lastMonthConcentricSlowRepsPercentage
    ) {
      return (
        <div className={styles.velocityPieColumn}>
          <span className={styles.emptyStat}>-</span>
        </div>
      );
    }
    return (
      <div className={styles.velocityPieColumn}>
        <VelocityPie
          good={lastMonthConcentricGoodRepsPercentage}
          fast={lastMonthConcentricFastRepsPercentage}
          slow={lastMonthConcentricSlowRepsPercentage}
          missed={userStatistics?.lastMonthConcentricMissedRepsPercentage}
          subtitle={texts.last30Days}
        />
      </div>
    );
  };

  return (
    <>
      <div
        onClick={onSelect}
        className={classNames(styles.userWorkoutsRow, styles.workoutsListGrid, {
          [styles.selected]: selected,
        })}
      >
        {renderUserName()}
        {renderLastWorkoutVelocityPie()}
        {renderLastWeekVelocityPie()}
        {renderLastMonthVelocityPie()}
        {renderMoreOptions()}
      </div>
      {renderDeleteClientConfirmationDialog()}
      {renderAssignProgramOptionsDialog()}
      {renderProgramTemplateDialog()}
    </>
  );
};

export default UserWorkoutsRow;
